'use client';

import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import * as RechartsPrimitive from 'recharts';

type ChartConfig = Record<
  string,
  {
    label?: ReactNode;
    color?: string;
  }
>;

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = createContext<ChartContextProps | null>(null);

function useChart() {
  const context = useContext(ChartContext);
  if (context === null) {
    throw new Error('useChart must be used within a <ChartContainer />');
  }
  return context;
}

type ChartContainerProps = ComponentProps<'div'> & {
  readonly config: ChartConfig;
  readonly children: ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
};

function ChartContainer({ className, children, config, style, ...props }: ChartContainerProps) {
  const colorVars: Record<string, string> = {};
  for (const [key, item] of Object.entries(config)) {
    if (item.color !== undefined) {
      colorVars[`--color-${key}`] = item.color;
    }
  }

  const mergedStyle: CSSProperties = { ...colorVars, ...style };

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        className={cn(
          'flex aspect-video w-full justify-center text-xs',
          '[&_.recharts-cartesian-axis-tick_text]:fill-secondary',
          "[&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50",
          '[&_.recharts-curve.recharts-tooltip-cursor]:stroke-border',
          '[&_.recharts-surface]:outline-none',
          '[&_.recharts-layer]:outline-none',
          '[&_.recharts-sector]:outline-none',
          '[&_.recharts-layer:focus-visible]:outline-solid',
          '[&_.recharts-layer:focus-visible]:outline-1',
          '[&_.recharts-layer:focus-visible]:outline-ring',
          className,
        )}
        style={mergedStyle}
        {...props}
      >
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
}

const ChartTooltip = RechartsPrimitive.Tooltip;

type ChartTooltipPayloadItem = {
  readonly dataKey?: string | number;
  readonly name?: string | number;
  readonly value?: string | number;
  readonly color?: string;
  readonly payload?: Record<string, unknown>;
};

type ChartTooltipContentProps = {
  readonly active?: boolean;
  readonly payload?: readonly ChartTooltipPayloadItem[];
  readonly label?: ReactNode;
  readonly className?: string;
  readonly hideLabel?: boolean;
  readonly hideIndicator?: boolean;
  readonly indicator?: 'line' | 'dot';
  readonly labelFormatter?: (label: ReactNode) => ReactNode;
  readonly formatter?: (
    value: ChartTooltipPayloadItem['value'],
    name: ChartTooltipPayloadItem['name'],
    item: ChartTooltipPayloadItem,
  ) => ReactNode;
};

function renderTooltipLabel(
  label: ReactNode,
  hideLabel: boolean,
  labelFormatter: ChartTooltipContentProps['labelFormatter'],
): ReactNode {
  if (hideLabel) return null;
  if (labelFormatter === undefined) return label;
  return labelFormatter(label);
}

function ChartTooltipContent({
  active,
  payload,
  label,
  className,
  hideLabel = false,
  hideIndicator = false,
  indicator = 'dot',
  labelFormatter,
  formatter,
}: ChartTooltipContentProps) {
  const { config } = useChart();

  if (active !== true || isNil(payload) || payload.length === 0) {
    return null;
  }

  const renderedLabel = renderTooltipLabel(label, hideLabel, labelFormatter);

  return (
    <div
      className={cn(
        'grid min-w-[8rem] items-start gap-1.5 rounded-md border border-light bg-popover px-2.5 py-1.5 text-xs shadow-md',
        className,
      )}
    >
      {!isNil(renderedLabel) ? <div className="font-medium text-popover-foreground">{renderedLabel}</div> : null}
      <div className="grid gap-1.5">
        {payload.map((item, index) => {
          const key = `${item.dataKey ?? item.name ?? index}`;
          const itemConfig = config[key];
          const indicatorColor = item.color ?? `var(--color-${key})`;
          return (
            <div key={key} className="flex w-full items-center gap-2">
              {hideIndicator ? null : (
                <span
                  className={cn('shrink-0 rounded-xs', indicator === 'dot' ? 'size-2.5' : 'h-2.5 w-1')}
                  style={{ backgroundColor: indicatorColor }}
                />
              )}
              <div className="flex flex-1 items-center justify-between gap-4 leading-none">
                <span className="text-secondary">{itemConfig?.label ?? item.name}</span>
                {!isNil(item.value) ? (
                  <span className="font-medium tabular-nums text-foreground">
                    {formatter !== undefined ? formatter(item.value, item.name, item) : item.value}
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { ChartContainer, ChartTooltip, ChartTooltipContent, useChart };
export type { ChartConfig, ChartTooltipContentProps, ChartTooltipPayloadItem };
